import { useCallback, useEffect, useEffectEvent, useMemo, useRef } from "react";
import { useNodesState } from "@xyflow/react";
import { OwnershipFlowNode } from "../ownership.types";

/**
 * React Flow owns node state (a controlled flow must drive dragging through
 * onNodesChange, or dragged nodes de-initialize and their edges vanish — RF
 * error #015). We seed/resync from the page's `nodes` except mid-drag, when
 * RF's own positions are authoritative.
 */
export function useSyncedFlowNodes(nodes: OwnershipFlowNode[]) {
    const [rfNodes, setRfNodes, onNodesChange] =
        useNodesState<OwnershipFlowNode>(nodes);
    const isDraggingRef = useRef(false);

    // Only the structural shape matters for reseeding React Flow's node store —
    // selection is layered on by the canvas. Resyncing on every `nodes`
    // identity change would drop measured dimensions on each selection.
    const nodesShapeKey = useMemo(
        () =>
            JSON.stringify(
                nodes.map((node) => [
                    node.id,
                    node.type,
                    node.position.x,
                    node.position.y,
                    node.width ?? null,
                    node.height ?? null,
                    node.parentId ?? null,
                ]),
            ),
        [nodes],
    );
    const lastSyncedShapeRef = useRef<string | null>(null);

    const resyncNodes = useEffectEvent(() => {
        setRfNodes(nodes);
    });
    useEffect(() => {
        if (
            isDraggingRef.current ||
            nodesShapeKey === lastSyncedShapeRef.current
        ) {
            return;
        }
        lastSyncedShapeRef.current = nodesShapeKey;
        resyncNodes();
    }, [nodesShapeKey]);

    // A drag moves RF's store away from the last synced shape, so forget it —
    // otherwise returning to the pre-drag shape (e.g. resetting the layout)
    // looks already-synced and the dragged node never moves back.
    const startDrag = useCallback(() => {
        isDraggingRef.current = true;
        lastSyncedShapeRef.current = null;
    }, []);

    const endDrag = useCallback(() => {
        isDraggingRef.current = false;
    }, []);

    return { rfNodes, onNodesChange, isDraggingRef, startDrag, endDrag };
}
